import React, { useEffect, useState, useRef } from 'react';
import { 
  FaUserCircle, FaBell, FaSearch, FaBars, FaTimes, 
  FaPhoneAlt, FaSun, FaMoon, FaCheckDouble 
} from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import useDarkMode from '../../../hooks/useDarkMode'; 

interface NavbarProps {
  onMenuToggle: () => void;
}

interface Notification {
  id: string;
  message: string;
  type: string;
  isRead: boolean;
  lead: { id: string; firstName: string; lastName: string };
  createdAt: string;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuToggle }) => {
  const [userName, setUserName] = useState<string>('Guest');
  const [userRole, setUserRole] = useState<string>('');
  const [userId, setUserId] = useState<string | null>(null);
  
  const [colorTheme, setTheme] = useDarkMode();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && typeof token === 'string') {
      try {
        const decodedToken: any = jwtDecode(token);
        setUserName(decodedToken.sub || decodedToken.name || 'User');
        setUserRole(decodedToken.role || '');
        const id = decodedToken.id || decodedToken.userId || localStorage.getItem('userId');
        setUserId(id);
      } catch (e) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  }, [navigate]);

  const getUsernameFromToken = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload).sub;
    } catch (error) { return null; }
  };

  const fetchNotifications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let url = '';
    const timestamp = new Date().getTime(); 

    if (userId) {
        url = `https://ayushvatika.com/api/notifications/recent/${userId}?t=${timestamp}`;
    } else {
        const username = getUsernameFromToken(token);
        if (username) {
            url = `https://ayushvatika.com/api/notifications/unread-by-user/${username}?t=${timestamp}`;
        } else {
            return;
        }
    }

    try {
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error("Error fetching notifications");
    }
  };

  useEffect(() => {
      fetchNotifications(); 
      const interval = setInterval(fetchNotifications, 10000); 
      return () => clearInterval(interval);
  }, [userId]);

  // ✅ Mark as Read / Dismiss (Cross button se chalega)
  const handleDismiss = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await fetch(`https://ayushvatika.com/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update UI: Mark local as read
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, isRead: true } : n
      ));
    } catch (error) {
        console.error("Error dismissing", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTheme = () => {
    setTheme(colorTheme);
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div 
        className={`w-full h-16 fixed top-0 left-0 right-0 flex items-center justify-between px-5 shadow-md z-40 transition-colors duration-300
        bg-white text-gray-800 
        dark:bg-gray-800 dark:text-white
        lg:w-[calc(100%-20rem)] lg:left-80`}
    >
      
      <button onClick={onMenuToggle} className="lg:hidden text-2xl mr-4 hover:text-gray-500 dark:hover:text-gray-300">
        <FaBars />
      </button>

      <div className="flex items-center relative flex-grow max-w-xs hidden sm:flex">
        <input
          type="text"
          placeholder="Search..."
          className="border p-2 pl-4 pr-10 rounded-full outline-none w-full text-sm transition-colors duration-300
          border-gray-300 bg-gray-100 text-gray-900 placeholder-gray-500 focus:border-blue-500
          dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-white"
        />
        <button className="absolute right-3 text-lg transition-colors text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white">
          <FaSearch />
        </button>
      </div>
      
      <div className="flex items-center gap-4">
        
        {/* --- NOTIFICATION BELL --- */}
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setShowDropdown(!showDropdown)} 
                className="text-xl mx-2 cursor-pointer transition-colors relative focus:outline-none
                text-gray-600 hover:text-gray-800 
                dark:text-white dark:hover:text-gray-300"
            >
                <FaBell size={24} />
                {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full animate-pulse border border-white dark:border-gray-800 shadow-sm">
                        {unreadCount}
                    </span>
                )}
            </button>

            {showDropdown && (
                <div className="absolute right-0 mt-4 w-80 rounded-lg shadow-xl overflow-hidden z-50 ring-1 ring-black ring-opacity-5
                bg-white border border-gray-200
                dark:bg-gray-800 dark:border-gray-600">
                    <div className="px-4 py-2 flex justify-between items-center border-b
                    bg-gray-100 border-gray-200 text-gray-800
                    dark:bg-gray-900 dark:border-gray-700 dark:text-white">
                        <h3 className="text-sm font-bold">Reminders</h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                           {unreadCount} Unread
                        </span>
                    </div>
                    
                    <div className="max-h-80 overflow-y-auto custom-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No new reminders</div>
                        ) : (
                            notifications.map(n => (
                                <div 
                                    key={n.id} 
                                    // 🚫 CLICK HANDLER REMOVED (Non-clickable)
                                    className={`px-4 py-3 border-b flex justify-between items-start transition-colors group
                                        border-gray-200 
                                        dark:border-gray-700 
                                        ${n.isRead 
                                            ? 'bg-gray-100 dark:bg-gray-900 opacity-60' 
                                            : 'bg-white dark:bg-gray-800'}
                                        ${n.type === '5M_REMINDER' && !n.isRead
                                            ? 'border-l-4 border-l-red-500' 
                                            : 'border-l-4 border-l-transparent'}
                                    `}
                                    style={{ cursor: 'default' }} // Cursor default kar diya
                                >
                                    <div className="flex-1 pr-2">
                                        <p className={`text-sm mb-1 ${n.isRead ? 'text-gray-500 font-normal' : 'text-gray-800 dark:text-gray-200 font-bold'}`}>
                                            {n.message}
                                        </p>
                                        <p className="text-xs flex items-center text-gray-500 dark:text-gray-400">
                                            <FaPhoneAlt className="mr-1" size={10}/> 
                                            {n.lead?.firstName} {n.lead?.lastName}
                                        </p>
                                    </div>
                                    
                                    {/* Dismiss / Mark as Read Button */}
                                    {!n.isRead ? (
                                        <button 
                                            onClick={(e) => handleDismiss(e, n.id)}
                                            className="p-1 cursor-pointer opacity-60 hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                                            title="Mark as Read / Remove"
                                        >
                                            <FaTimes />
                                        </button>
                                    ) : (
                                        <FaCheckDouble className="text-green-500 text-xs mt-1" title="Read" />
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>

        <button 
            onClick={toggleTheme} 
            className="text-xl mx-2 p-2 rounded-full transition-colors hover:bg-gray-200 dark:hover:bg-gray-700 text-yellow-500 dark:text-gray-300"
            title="Toggle Theme"
        >
            {colorTheme === 'light' ? <FaSun size={20} /> : <FaMoon size={18} />}
        </button>

        <div className="flex items-center ml-4 pl-4 border-l border-gray-300 dark:border-gray-600">
          <FaUserCircle className="w-8 h-8 mr-2 text-gray-600 dark:text-white" />
          <div className='hidden md:block'> 
            <p className="font-bold text-sm leading-tight text-gray-800 dark:text-white">{userName}</p>
            <p className="text-xs leading-tight text-gray-500 dark:text-gray-400">{userRole}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;